import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Лайтбокс-галерея:
 *  - картинка вписывается (object-fit: contain, max 96vw/92vh)
 *  - зум колесом/даблкликом, панорамирование мышью/тачем
 *  - границы панорамирования зажаты
 *  - стрелки ← → и Esc
 *  - РЕНДЕР ЧЕРЕЗ PORTAL + stopPropagation, чтобы не закрывать родительские модалки
 */
export default function LightboxGallery({ open, images, startIndex = 0, onClose }) {
    const [idx, setIdx] = useState(startIndex);
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);

    const wrapRef = useRef(null);
    const imgRef = useRef(null);
    const lastPtr = useRef({ x: 0, y: 0, active: false });

    const count = images?.length || 0;
    const src = images?.[idx]?.src || "";
    const name = images?.[idx]?.name || "";

    useEffect(() => {
        if (!open) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prevOverflow; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            if (e.key === "ArrowRight") setIdx((i) => (i + 1) % count);
            if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + count) % count);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, count, onClose]);

    // сброс трансформаций при смене изображения
    useEffect(() => { setScale(1); setTx(0); setTy(0); }, [idx, src]);

    const clampPan = () => {
        const stage = wrapRef.current;
        const img = imgRef.current;
        if (!stage || !img) return;

        const vw = stage.clientWidth;
        const vh = stage.clientHeight;

        const naturalW = img.naturalWidth || vw;
        const naturalH = img.naturalHeight || vh;
        const ratioW = (vw * 0.96) / naturalW;
        const ratioH = (vh * 0.92) / naturalH;
        const baseScale = Math.min(ratioW, ratioH);

        const displayW = naturalW * baseScale * scale;
        const displayH = naturalH * baseScale * scale;

        const maxX = Math.max(0, (displayW - vw) / 2);
        const maxY = Math.max(0, (displayH - vh) / 2);

        setTx((x) => Math.max(-maxX, Math.min(maxX, x)));
        setTy((y) => Math.max(-maxY, Math.min(maxY, y)));
    };

    useLayoutEffect(clampPan, [scale, src]);

    const onWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.15 : -0.15;
        const ns = Math.max(1, Math.min(6, scale + delta));
        setScale(ns);
    };

    const onDouble = () => setScale((s) => (s > 1 ? 1 : 2.5));

    const onPointerDown = (e) => {
        lastPtr.current = { x: e.clientX, y: e.clientY, active: true };
        wrapRef.current?.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!lastPtr.current.active || e.buttons !== 1) return;
        setTx((v) => v + (e.clientX - lastPtr.current.x));
        setTy((v) => v + (e.clientY - lastPtr.current.y));
        lastPtr.current = { x: e.clientX, y: e.clientY, active: true };
    };
    const onPointerUp = () => {
        lastPtr.current.active = false;
        clampPan();
    };

    if (!open || !src) return null;

    const stopAll = (e) => { e.stopPropagation(); };

    return createPortal(
        <div
            className="lbx"
            onClick={stopAll}
            onMouseDown={stopAll}
            onPointerDown={stopAll}
        >
            <button className="lbx__close" onClick={onClose}>✕</button>

            <button
                className="lbx__nav lbx__nav--left"
                onClick={() => setIdx((i) => (i - 1 + count) % count)}
                disabled={count <= 1}
            >‹</button>
            <button
                className="lbx__nav lbx__nav--right"
                onClick={() => setIdx((i) => (i + 1) % count)}
                disabled={count <= 1}
            >›</button>

            <div
                className="lbx__stage"
                ref={wrapRef}
                onWheel={onWheel}
                onDoubleClick={onDouble}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
            >
                <img
                    ref={imgRef}
                    src={src}
                    alt={name}
                    className="lbx__img"
                    style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
                    draggable={false}
                    onLoad={clampPan}
                    onError={() => console.warn("[Lightbox] image error", src)}
                />
            </div>

            <div className="lbx__tools">
                <button onClick={() => setScale((s) => Math.min(6, s + 0.25))}>➕</button>
                <button onClick={() => setScale((s) => Math.max(1, s - 0.25))}>➖</button>
                <button onClick={() => { setScale(1); setTx(0); setTy(0); }}>Сброс</button>
                <span className="lbx__counter">{idx + 1} / {count}</span>
            </div>
        </div>,
        document.body
    );
}
